import React, { useState, useEffect } from 'react';
import { useTableUniverse, useTableApi } from '~Components/TableUniverse';
import Bong from '~Scenes/POS';
import { DISCOUNT_TYPE } from '~../__queries__';
import _ from 'lodash';
import { useInventoryApi, useInventoryUniverse } from '~Components/InventoryUniverse';

import { useButton, useI18n, useLogger, useSelector } from '~Hooks';
import { useCart } from '~Components/CartProvider';
import { extractServiceChargePercentage, isServiceChargeItem } from '~Utils/serviceCharge';
import { useCompetition } from '~Providers/CompetitionsProvider';
import { useEntryFinder } from '~Components/EntryFinder';
import { VenueData_venue_company_courseTypes } from '~Scenes/Tables/__queries__';
import { formatTicketMessage } from '~utilities';
import { GreenButton, WhiteButton } from '~Styles';
import styled from '@emotion/styled';
import { useItemsPackager } from '~Utils/packagedItems';

const ButtonWrapper = styled.div`
  margin: 16px;
`;

interface Props {
  chequeId: string;
  onCancel: () => void;
  venueId: string;
  table: {
    id: string;
    name: string;
    coverage?: number;
  } | null;
  chequeName: string | undefined;
  onSubmit?: () => void;
  allCourseTypes: VenueData_venue_company_courseTypes[];
}

type AppliedDiscount = {
  id?: string | undefined;
  amount?: number | undefined;
  percentage?: number | undefined;
} | null;

const BongWrapper: React.FunctionComponent<Props> = ({
  venueId,
  chequeId,
  chequeName,
  table,
  onCancel,
  onSubmit,
  allCourseTypes,
}) => {
  const cart = useCart();
  const [appliedDiscount, setAppliedDiscount] = useState<AppliedDiscount>(null);
  const [readyForAdding, setReadyForAdding] = useState(false);
  const staff = useSelector((state) => state.staff);
  const universe = useTableUniverse();
  const inventoryUniverse = useInventoryUniverse();
  const inventory = useInventoryApi(venueId);
  const competitions = useCompetition();
  const { i18n } = useI18n();
  const logger = useLogger('bongwrapper');
  const findEntryFromItem = useEntryFinder();

  const packagedItems = useItemsPackager(
    cart.items.filter((item) => !item.isMessage),
    venueId,
    appliedDiscount ? appliedDiscount.amount! || appliedDiscount.percentage! : null,
    (appliedDiscount?.amount
      ? DISCOUNT_TYPE.PRICE
      : appliedDiscount?.percentage
      ? DISCOUNT_TYPE.PERCENTAGE
      : null) || null,
  );
  const tableName = table ? table.name : undefined;

  const tableApi = useTableApi();

  useEffect(() => {
    if (!readyForAdding) {
      return;
    }

    /** Items that is filtered from Service Charge & Messages */
    const justRegularItems: typeof packagedItems = [];

    cart.packagedItems.forEach((item) => {
      if (isServiceChargeItem(item.title)) {
        const percentage = extractServiceChargePercentage(item.title);
        tableApi.addServiceCharge({
          categoryId: item.categoryId,
          chequeId,
          entryId: item.entryId,
          itemId: item.itemId,
          percentage,
          vatRate: item.vatRate,
        });
        return;
      }

      const invItem = inventoryUniverse.items.find((i) => i.itemId === item.itemId);
      if (invItem && typeof invItem.count === 'number') {
        inventory.reduceStock(invItem.itemId, item.fraction, 'Order');
      }

      // Messages are handled separately
      if (item.isMessage) {
        return;
      }

      justRegularItems.push(item);
    });

    const items = justRegularItems.map((item) => ({
      ...item,
      amount: item.originalAmount || item.amount, // The TSR handles the offers calculation so we need to send the original amount for it to calculate the discount correctly
      discount: 0, // The TSR handles the offers calculation so we need to send 0 discount for it to calculate the discount correctly
      entryId: findEntryFromItem(item.itemId).id,
    }));

    tableApi.addItems(items, chequeId);

    const messages = cart.items.filter((item) => item.isMessage);

    // If coverage isnt set, check one more time in case it was set after the bong was opened
    let coverage = table?.coverage;

    if (!coverage) {
      coverage = universe.cheques.find((c) => c.id === chequeId)?.coverage || undefined;
    }

    const itemsWithoutServiceCharge = packagedItems.filter(
      (item) => !isServiceChargeItem(item.title) && !item.isMessage,
    );

    if (staff?.id) {
      competitions.addSale({
        staffId: staff?.id,
        items: itemsWithoutServiceCharge,
      });
    }

    logger.info('[DEBUG MODE] Sending kitchen bong', {
      messages,
      packagedItems: itemsWithoutServiceCharge,
      tableName,
      chequeName,
      coverage,
      ticketMessages: cart.ticketMessages,
    });
    tableApi.sendKitchenBong(
      messages,
      itemsWithoutServiceCharge,
      tableName,
      chequeName,
      table?.id,
      coverage,
      undefined,
      undefined,
      cart.ticketMessages,
    );

    tableApi.log.addItems(chequeId, table?.id ?? '', itemsWithoutServiceCharge);

    cart.ticketMessages.forEach((tm) => {
      const courseType = allCourseTypes.find((ct) => ct.id === tm.courseId);
      const message = formatTicketMessage(tm.message, courseType?.label);
      tableApi.addMessage(message, chequeId);
    });

    messages.forEach((message) => {
      tableApi.addMessage(message.title, chequeId, message.courseType?.label);
    });

    if (onSubmit) {
      onSubmit();
    }

    onCancel();
  }, [readyForAdding]);

  function handleAddItems(appliedDiscount: AppliedDiscount) {
    setAppliedDiscount(appliedDiscount);
    setReadyForAdding(true);
  }

  const confirmButton = useButton({
    onClick: () => {
      handleAddItems(cart.appliedDiscount);
    },
  });

  const cancelButton = useButton({
    onClick: () => {
      onCancel();
    },
  });

  const chequeItems = universe.items.filter((item) => item.chequeId === chequeId);
  const chequeItemCountMap = _.countBy(chequeItems, 'itemId');
  const cheque = universe.cheques.find((ch) => ch.id === chequeId);

  return (
    <Bong
      isTsr
      orderId={null}
      leftItems={[]}
      paymentId={null}
      enableQuantityRefetch={false}
      onEnableQuantityRefetch={() => {}}
      venueId={venueId}
      animateOnMount
      alreadyInCartCountMap={chequeItemCountMap}
      buttons={
        <ButtonWrapper>
          {cart.items.length > 0 ? (
            <GreenButton {...confirmButton} fluid size="xl">
              {i18n('TSR.Confirm')}
            </GreenButton>
          ) : (
            <WhiteButton {...cancelButton} fluid size="xl">
              {i18n('TSR.Cancel')}
            </WhiteButton>
          )}
        </ButtonWrapper>
      }
      onClose={onCancel}
      tableId={table?.name ?? null}
      hasServiceCharge={Boolean(cheque?.serviceCharge)}
    />
  );
};

export default BongWrapper;
